import moment from 'moment/moment'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import agent from 'agent'
import HaltConfirmation from 'components/attacks/HaltConfirmation'
import BusyOverlay from 'components/common/BusyOverlay'
import Button from 'components/common/Button'
import CardTooltip from 'components/common/CardTooltip'
import { ConfirmationModal } from 'components/common/ConfirmationModal'
import OffCanvas from 'components/common/OffCanvas'
import Popover from 'components/common/Popover'
import PopoverMenu from 'components/common/PopoverMenu'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Tooltip from 'components/common/Tooltip'
import { isScenarioUsableForGameDay } from 'components/gamedays/gameday-utils'
import Icon from 'components/Icon'
import { DeleteScenarioButton } from 'components/scenarios/DeleteScenarioButton'
import { ResultsForm } from 'components/scenarios/detail/ResultsForm'
import ScheduleWrapper from 'components/scenarios/detail/ScheduleWrapper'
import RerunConfirmationModal from 'components/scenarios/RerunConfirmationModal'
import { ShareScenarioModal } from 'components/scenarios/ShareScenarioModal'
import { hasBeenRun } from 'components/scenarios/utils'
import { arrayFromGraph } from 'components/scenarios/utils/graph'
import useAuth from 'hooks/use-auth'
import { useHasProductionTargetsScenario } from 'hooks/use-is-production-service'
import { useRestrictedTimeWindow } from 'hooks/use-restricted-time-window'
import { trans } from 'i18n/translate'
import toast from 'lib/toast'
import theme from 'theme'
import { RbacPrivilege } from 'types/api/rbac/rbac'
import { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'
import { Scenario } from 'types/api/scenarios/scenario'
import { ScheduledScenario } from 'types/schedules/scheduled-scenarios'
import { messageFromResponseError } from 'utils/strings'

const EditScenarioButton: React.FC<{
  scenario: Scenario
  doFetch?: () => Promise<void>
}> = ({ scenario }) => {
  const navigate = useNavigate()
  return (
    <>
      <Button
        data-test-edit-button
        iconOnly
        secondary
        data-track-action="edit-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={scenario.state === 'ARCHIVED'}
        title="Edit Scenario"
        onClick={() => {
          navigate('/scenarios/edit', {
            state: {
              scenario,
              backtrack: `/scenarios/detail/${scenario.guid}`
            }
          })
        }}
      >
        <Icon css={{ fontSize: theme.fontSizes[3] }} icon="pencil" />
      </Button>
    </>
  )
}

const ScheduleScenarioButton: React.FC<{
  scenario: Scenario
  schedules: ScheduledScenario[]
  doFetch: () => Promise<void>
}> = ({ scenario, schedules, doFetch }) => {
  const [isOffCanvasOpen, setIsOffCanvasOpen] = useState(false)
  const steps = arrayFromGraph(scenario.graph)
  const isDisabled =
    !hasBeenRun(scenario) || steps.length < 1 || scenario.state === 'ARCHIVED'
  return (
    <>
      <Button
        data-test-modify-schedule
        data-test-schedule-button
        iconOnly
        secondary
        data-track-action="schedule-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={isDisabled}
        title={
          isDisabled
            ? 'Add at least one step and run this Scenario once before you can schedule it'
            : 'Edit schedule'
        }
        onClick={() => setIsOffCanvasOpen(true)}
      >
        <Icon css={{ fontSize: theme.fontSizes[4] }} icon="calendar" />
      </Button>
      <OffCanvas
        open={isOffCanvasOpen}
        setIsOpen={setIsOffCanvasOpen}
        title="Schedule Scenario"
      >
        <ScheduleWrapper
          fetchScenario={doFetch}
          scenarioId={scenario.guid}
          schedule={schedules[0]}
          setScheduleOffCanvas={setIsOffCanvasOpen}
        />
      </OffCanvas>
    </>
  )
}

const ShareScenarioButton: React.FC<{ scenario: Scenario }> = ({
  scenario
}) => {
  const { currentTeamId } = useAuth()
  const [isShareModalOpen, setShareModalOpen] = useState(false)
  const steps = arrayFromGraph(scenario.graph)
  const isDisabled = !hasBeenRun(scenario) || steps.length < 1

  return (
    <>
      <Button
        iconOnly
        secondary
        data-track-action="share-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={isDisabled}
        title={
          isDisabled
            ? 'Add at least one step and run this Scenario once before you can share it'
            : 'Share scenario'
        }
        onClick={() => setShareModalOpen(true)}
      >
        <Icon css={{ fontSize: theme.fontSizes[4] }} icon="share" />
      </Button>
      <ShareScenarioModal
        currentTeamId={currentTeamId}
        open={isShareModalOpen}
        scenarioId={scenario.guid}
        onCancel={() => setShareModalOpen(false)}
        onConfirm={() => setShareModalOpen(false)}
      />
    </>
  )
}

export const RevertScenarioButton: React.FC<{
  scenario: Scenario
  run: ScenarioRun
}> = ({ scenario, run, ...rest }) => {
  const navigate = useNavigate()

  return (
    <Button
      data-test-revert-button
      secondary
      data-track-action="revert-scenario"
      data-track-category="scenario-details"
      data-track-on="click"
      onClick={() => {
        navigate('/scenarios/edit', {
          state: {
            scenario: {
              ...scenario,
              ...run
            },
            backtrack: `/scenarios/detail/${scenario.guid}/runs/${run.run_number}`
          }
        })
      }}
      {...rest}
    >
      Revert to this version
    </Button>
  )
}

const ButtonWrapper: React.FC<{
  isDraftState: boolean
  disabled?: boolean
  title?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}> = ({ isDraftState, ...rest }) => (
  <Button
    data-test-run-button
    data-track-action={isDraftState ? 'run-scenario' : 'rerun-scenario'}
    data-track-category="scenario-details"
    data-track-on="click"
    {...rest}
  >
    {isDraftState ? 'Run Scenario' : 'Rerun Scenario'}
  </Button>
)

export const RunScenarioButton: React.FC<{
  onRunScenario?: (scenario: Scenario) => void
  scenario: Scenario
  doFetch: () => Promise<void>
}> = ({ onRunScenario, scenario, doFetch }) => {
  const { currentTeamId, isProductionEnvironment } = useAuth()
  const { hasProductionTargets } = useHasProductionTargetsScenario(scenario)
  const { isRestrictedTimeWindowActive, windowEnd } = useRestrictedTimeWindow()
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const steps = arrayFromGraph(scenario.graph)
  const isDraftState = scenario.state === 'DRAFT'
  const isArchiveState = scenario.state === 'ARCHIVED'
  const isRunnable = steps.length > 0 && !isRestrictedTimeWindowActive

  if (isArchiveState) {
    return (
      <Popover alignPopover={Popover.ALIGN_BOTTOM}>
        {({ getTargetProps, getPopoverProps }) => (
          <div css={{ display: 'inline-block' }} {...getTargetProps()}>
            <ButtonWrapper
              disabled
              css={{ pointerEvents: 'none' }}
              isDraftState={isDraftState}
              title={null}
            />
            <CardTooltip
              displayArrow
              variation="dark"
              {...getPopoverProps({
                css: {
                  maxWidth: 250
                }
              })}
            >
              <Text as="p" color="white">
                Unarchive this Scenario to run it again
              </Text>
            </CardTooltip>
          </div>
        )}
      </Popover>
    )
  }

  const runScenarioDefault = async () => {
    let runResponse
    try {
      runResponse = await agent.post(
        `/scenarios/${scenario.guid}/runs?teamId=${currentTeamId}`,
        {}
      )
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was an issue running this scenario. Please try again later.'
        )
      })
      setIsModalOpen(false)
      return
    }
    await doFetch()
    setIsModalOpen(false)

    // The runs tab will redirect to the latest run by default
    const runNumber = runResponse?.data ?? ''
    setTimeout(() => {
      navigate(`/scenarios/detail/${scenario.guid}/runs/${runNumber}`)
    }, 300)
  }

  const isProduction = isProductionEnvironment || hasProductionTargets

  return (
    <>
      <Tooltip
        contents={() => {
          return (
            <Text as="p" color="white">
              {isRestrictedTimeWindowActive
                ? trans('restricted_time_window.alert_text', {
                    endTime: moment
                      .utc(windowEnd, 'HH:mm')
                      .format('h:mm A, dddd')
                  })
                : trans(
                    'scenarios.detail.scenario_actions.run_scenario_button.tooltip.text'
                  )}
            </Text>
          )
        }}
        tooltipEnabled={!isRunnable}
      >
        <ButtonWrapper
          css={{
            pointerEvents: isRunnable ? 'initial' : 'none'
          }}
          disabled={!isRunnable}
          isDraftState={isDraftState}
          onClick={() => setIsModalOpen(true)}
        />
      </Tooltip>

      {isProduction ? (
        <ConfirmationModal
          confirmText="Run Scenario"
          message={
            <>
              <Text as="p">
                This scenario has targets in your production environment. Your
                system may be impacted. You can halt the scenario while it's in
                progress if needed.{' '}
              </Text>
              <br />
              <Text as="p">
                {isDraftState
                  ? 'Are you sure you want to run this scenario?'
                  : 'Are you sure you want to re-run this scenario?'}
              </Text>
            </>
          }
          open={isModalOpen}
          title={
            isDraftState
              ? 'Run scenario in production?'
              : 'Re-run scenario in production?'
          }
          onCancel={() => setIsModalOpen(false)}
          onConfirm={async () => {
            if (onRunScenario) {
              onRunScenario(scenario)
            } else {
              runScenarioDefault()
            }
          }}
        />
      ) : (
        <RerunConfirmationModal
          isDraftState={isDraftState}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onConfirm={() => {
            if (onRunScenario) {
              onRunScenario(scenario)
            } else {
              runScenarioDefault()
            }
          }}
        />
      )}
    </>
  )
}

export const ScenarioHaltConfirmationModal: React.FC<{
  haltConfirmationOpen: boolean
  setHaltConfirmationOpen:
    | Dispatch<SetStateAction<ScenarioRun>>
    | Dispatch<SetStateAction<boolean>>
  setDisableButton(b?: boolean): void
  selectedRun: ScenarioRun
  scenarioId: string
  title?: string | React.ReactNode
  isCompletedNotification?: boolean
  doFetch(): void
}> = ({
  haltConfirmationOpen,
  setHaltConfirmationOpen,
  setDisableButton,
  selectedRun,
  doFetch,
  scenarioId,
  title,
  isCompletedNotification,
  ...rest
}) => {
  const [isHalting, setIsHalting] = useState(false)
  const { currentTeamId } = useAuth()

  // @ts-ignore TODO: Fix as part of the TS migration
  const onConfirmRequest = async ({ reason } = {}) => {
    const body = !reason ? {} : { reason }
    setDisableButton(true)
    try {
      setIsHalting(true)
      await agent.post(
        `/scenarios/halt/${scenarioId}/runs/${selectedRun.run_number}?teamId=${currentTeamId}`,
        body
      )
      setIsHalting(false)
      setDisableButton(false)
      setHaltConfirmationOpen(false)
      toast({
        type: 'success',
        text: 'Scenario successfully halted'
      })
      await doFetch()
    } catch (err) {
      setDisableButton(false)
      setHaltConfirmationOpen(false)
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was an issue halting your scenario. Please try again later'
        )
      })
    }
  }

  return isHalting ? (
    <BusyOverlay shown={true} text="Halting..." />
  ) : (
    <HaltConfirmation
      hideFooter
      doFetch={doFetch}
      message={
        <ResultsForm
          isConfirmationModal
          doFetch={doFetch}
          footerOptions={{
            confirmText: isCompletedNotification ? 'Save' : 'Confirm',
            cancelText: 'Cancel',
            confirmButtonType: isCompletedNotification
              ? 'primary'
              : 'destructive',
            cancelButtonType: 'secondary'
          }}
          hideSave={true}
          isCompletedNotification={isCompletedNotification}
          runInfo={selectedRun}
          showStatusCheckPrompt={isCompletedNotification}
          onCancel={() => setHaltConfirmationOpen(false)}
          onConfirm={
            isCompletedNotification
              ? () => setHaltConfirmationOpen(false)
              : onConfirmRequest
          }
        />
      }
      open={haltConfirmationOpen}
      showConfirmationContent={false}
      title={title || 'Halt scenario'}
      trackedEvent={
        isCompletedNotification ? 'ScenarioCompleted' : 'SingleScenarioHalted'
      }
      trackingOptions={{
        scenarioId: scenarioId,
        runNumber: selectedRun.run_number
      }}
      type="scenario"
      onCancel={() => {
        setHaltConfirmationOpen(false)
      }}
      onConfirmRequest={onConfirmRequest}
      {...rest}
    />
  )
}

export const HaltScenarioButton: React.FC<{
  scenario: Scenario
  selectedRun: ScenarioRun
  doFetch: () => Promise<void>
}> = ({ scenario, selectedRun, doFetch }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isHaltButtonDisabled, setIsHaltButtonDisabled] = useState(false)

  return (
    <>
      <ScenarioHaltConfirmationModal
        doFetch={doFetch}
        haltConfirmationOpen={isModalOpen}
        scenarioId={scenario.guid}
        selectedRun={selectedRun}
        setDisableButton={setIsHaltButtonDisabled}
        setHaltConfirmationOpen={setIsModalOpen}
      />
      <Button
        data-test-halt-button
        destructive
        data-track-action="halt-scenario"
        data-track-category="scenario-details"
        data-track-on="click"
        disabled={isHaltButtonDisabled}
        onClick={() => setIsModalOpen(true)}
      >
        {isHaltButtonDisabled ? 'Halting...' : 'Halt Scenario'}
      </Button>
    </>
  )
}

export const ScenarioHeaderButtonsPopover: React.FC<{ scenario: Scenario }> = ({
  scenario
}) => {
  const navigate = useNavigate()
  const canCreateGameDayFromScenario = isScenarioUsableForGameDay(scenario)

  return (
    <Popover
      openOnClick
      alignPopover={Popover.ALIGN_RIGHT}
      arrowSize={theme.space[4]}
      topBuffer={theme.space[7]}
      onClick={(e) => e.preventDefault()}
    >
      {({ getTargetProps, getPopoverProps }) => (
        <>
          <Icon
            data-test-scenario-popover-actions
            icon="solidEllipsisVertical"
            {...getTargetProps({
              css: {
                fontSize: theme.fontSizes[3],
                color: theme.colors.steel[0],
                cursor: 'pointer',

                '&:hover': {
                  color: theme.colors.steel[2]
                }
              }
            })}
          />
          <PopoverMenu getPopoverProps={getPopoverProps}>
            {canCreateGameDayFromScenario && (
              <TextButton
                data-test-create-gameday-button
                secondary
                data-track-action="create-gameday-from-scenario"
                data-track-category="scenario-details"
                data-track-on="click"
                onClick={(event) => {
                  event.preventDefault()
                  navigate('/gamedays/new', {
                    state: {
                      scenarioId: scenario.guid
                    }
                  })
                }}
              >
                Create GameDay from Scenario
              </TextButton>
            )}
            <TextButton
              data-test-duplicate-button
              secondary
              data-track-action="duplicate-scenario"
              data-track-category="scenario-details"
              data-track-on="click"
              onClick={(event) => {
                event.preventDefault()
                navigate('/scenarios/new', {
                  state: {
                    scenario
                  }
                })
              }}
            >
              Duplicate
            </TextButton>
            <DeleteScenarioButton scenario={scenario} />
          </PopoverMenu>
        </>
      )}
    </Popover>
  )
}

export const ScenarioHeaderBaseActions: React.FC<{
  scenario: Scenario
  schedules: ScheduledScenario[]
  doFetch: () => Promise<void>
}> = ({ scenario, schedules, doFetch }) => {
  const { hasTeamPrivilege, currentTeamId } = useAuth()

  return (
    <>
      <EditScenarioButton doFetch={doFetch} scenario={scenario} />
      <ScheduleScenarioButton
        doFetch={doFetch}
        scenario={scenario}
        schedules={schedules}
      />
      {hasTeamPrivilege(currentTeamId, RbacPrivilege.SHARE_SCENARIO) && (
        <ShareScenarioButton scenario={scenario} />
      )}
      <RunScenarioButton doFetch={doFetch} scenario={scenario} />
    </>
  )
}
